import type { TimeFrequency } from '@epilot/pricing'
import {
  formatPriceUnit,
  formatFeeAmountFromString,
  getAmountWithTax
} from '@epilot/pricing'
import type { TFunction } from 'i18next'

import classes from './BaseTable.module.scss'
import { usePriceGetAgDetailsContext } from './PriceGetAgDetailsContext'
import { getYearlyDecimalAmount } from './utils'

export type PriceTableRow = {
  label: string
  isEmpty?: boolean
  tax?: number
  yearlyAmountDecimal?: string
  // amounts based on the billing period
  amountDecimal?: string
  unitAmountDecimal?: string
}

type BaseTableProps = {
  title: string
  rows: PriceTableRow[]
}

export const BaseTable = ({ title, rows }: BaseTableProps) => {
  const { t, userLocale, currency, billingPeriod, unit } =
    usePriceGetAgDetailsContext()

  // Determine if we have 3 or 4 columns based on billing period
  const hasYearlyColumn = billingPeriod !== 'yearly'
  const tableClassName = hasYearlyColumn
    ? classes.priceTable
    : `${classes.priceTable} ${classes.threeColumns}`

  return (
    <div className={classes.tableContainer}>
      <table className={tableClassName}>
        <thead>
          <tr>
            <th title={t(`price_get_ag_details.table.groups.${title}`, title)}>
              {t(`price_get_ag_details.table.groups.${title}`, title)}
            </th>
            <th
              title={t('getag_details_table.price_per_unit', 'Price/{{unit}}', {
                unit: formatPriceUnit(unit)
              })}
            >
              {t('getag_details_table.price_per_unit', 'Price/{{unit}}', {
                unit: formatPriceUnit(unit)
              })}
            </th>
            <th
              title={t(
                `table_order.recurrences.billing_period.${billingPeriod}`,
                billingPeriod
              )}
            >
              {t(
                `table_order.recurrences.billing_period.${billingPeriod}`,
                billingPeriod
              )}
            </th>
            {hasYearlyColumn && (
              <th
                title={t(
                  'table_order.recurrences.billing_period.yearly',
                  'yearly'
                )}
              >
                {t('table_order.recurrences.billing_period.yearly', 'yearly')}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <TableRow
              billingPeriod={billingPeriod}
              currency={currency}
              hasYearlyColumn={hasYearlyColumn}
              index={index}
              key={row.label || index}
              row={row}
              t={t}
              userLocale={userLocale}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

const TableRow = ({
  row,
  index,
  hasYearlyColumn,
  billingPeriod,
  t,
  userLocale,
  currency
}: {
  row: PriceTableRow
  hasYearlyColumn: boolean
  billingPeriod: TimeFrequency
  t: TFunction
  index: number
  userLocale: string
  currency: string
}) => {
  const amountDecimal = row.tax
    ? getAmountWithTax(row.amountDecimal, row.tax)
    : row.amountDecimal

  const unitAmountDecimal = row.tax
    ? getAmountWithTax(row.unitAmountDecimal, row.tax)
    : row.unitAmountDecimal

  const yearlyAmountDecimal = row.yearlyAmountDecimal
    ? row.tax
      ? getAmountWithTax(row.yearlyAmountDecimal, row.tax)
      : row.yearlyAmountDecimal
    : undefined

  return (
    <tr key={row.label || index}>
      <td>{t(row.label, row.label)}</td>
      <td>
        {!row.isEmpty && unitAmountDecimal
          ? formatFeeAmountFromString({
              decimalAmount: unitAmountDecimal,
              currency,
              locale: userLocale,
              enableSubunitDisplay: true
            })
          : '-'}
      </td>
      <td>
        {!row.isEmpty && amountDecimal
          ? formatFeeAmountFromString({
              decimalAmount: amountDecimal,
              currency,
              locale: userLocale,
              enableSubunitDisplay: true
            })
          : '-'}
      </td>
      {hasYearlyColumn && (
        <td>
          {!row.isEmpty && amountDecimal
            ? formatFeeAmountFromString({
                decimalAmount: yearlyAmountDecimal
                  ? yearlyAmountDecimal
                  : getYearlyDecimalAmount(amountDecimal, billingPeriod),
                currency,
                locale: userLocale
              })
            : '-'}
        </td>
      )}
    </tr>
  )
}
